import React, { useEffect } from 'react';
import { NavigationContainer, LinkingOptions } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createStackNavigator } from '@react-navigation/stack';
import { ActivityIndicator, View, Platform, StyleSheet } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import {
  SplashScreen,
  AuthScreen,
  RegisterScreen,
  RegisterStep2Screen,
  RegisterStep3Screen,
  RatingSummaryScreen,
  ParticipantRatingScreen,
  ChatConversationScreen,
  CreateAnnouncementScreen,
} from '@/screens';
import ApplicationSettingsScreen from '@/screens/ApplicationSettingsScreen';
import ApplicationCreateScreen from '@/screens/ApplicationCreateScreen';
import ApplicationSentScreen from '@/screens/ApplicationSentScreen';
import AnnouncementApplicationsScreen from '@/screens/AnnouncementApplicationsScreen';
import UserProfileScreen from '@/screens/UserProfileScreen';
import ContactDetailsScreen from '@/screens/ContactDetailsScreen';
import MyProfileScreen from '@/screens/MyProfileScreen';
import MyAnnouncementsScreen from '@/screens/MyAnnouncementsScreen';
import MyApplicationsScreen from '@/screens/MyApplicationsScreen';
import MyApplicationsCalendarScreen from '@/screens/MyApplicationsCalendarScreen';
import WalletScreen from '@/screens/WalletScreen';
import TabNavigator from './TabNavigator';
import type { RootStackParamList } from '@/types/navigation';
// Import screen configs only for native - web uses simpler options
const getScreenConfig = (configName: string) => {
  if (Platform.OS === 'web') {
    return {};
  }
  // Dynamically import to avoid type conflicts
  const { SCREEN_CONFIGS } = require('./animations');
  return SCREEN_CONFIGS[configName]?.() || {};
};
import { useAuth } from '@/contexts/AuthContext';
import { navigationRef, resetToAuth } from './navigationRef';
import { setOnUnauthorized } from '@/api/client';

// Use regular stack navigator on web (native-stack uses react-native-screens which has issues on web)
const NativeStack = createNativeStackNavigator<RootStackParamList>();
const WebStack = createStackNavigator<RootStackParamList>();
const Stack = Platform.OS === 'web' ? WebStack : NativeStack;

// Linking configuration - works for both web and mobile
const linking: LinkingOptions<RootStackParamList> = {
  prefixes:
    Platform.OS === 'web'
      ? ['https://tino.app', 'http://localhost:3000']
      : ['tino://'],
  config: {
    screens: {
      Splash: 'welcome',
      Auth: 'auth',
      Register: 'register',
      RegisterStep2: 'register/step2',
      RegisterStep3: 'register/step3',
      MyProfile: 'profile-tab', // /profile-tab shows MyProfileScreen (Mój profil w ustawieniach) - must be before TabNavigator
      TabNavigator: {
        screens: {
          Home: '',
          Chat: 'chat',
          Create: 'create',
          Profile: 'profile-settings', // Tab Profile
          Settings: 'settings',
        },
      },
      ApplicationSettings: 'application-settings',
      ContactDetails: 'contact/:id',
      RatingSummary: 'ratings',
      ParticipantRating: 'ratings/:id',
      ChatConversation: 'chat/:id',
      MyAnnouncements: 'my-announcements',
      MyApplications: 'my-applications',
      MyApplicationsCalendar: 'my-applications/calendar',
      Wallet: 'wallet',
      ApplicationCreate: 'application/create/:announcementId',
      ApplicationSent: 'application/sent/:announcementId',
      AnnouncementApplications: 'announcement/:id/applications',
      UserProfile: 'user/:userId',
      CreateAnnouncement: 'create-announcement',
    },
  },
};

// Web-specific screen options
const getWebScreenOptions = () => ({
  animationEnabled: false,
  cardStyle: { flex: 1 },
  detachPreviousScreen: false,
});

// Native-specific screen options
const getNativeScreenOptions = () => ({
  gestureEnabled: true,
  gestureDirection: 'horizontal' as const,
});

const AppNavigator: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    setOnUnauthorized(() => {
      resetToAuth();
    });
  }, []);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Unified navigation for both web and mobile
  const content = (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      documentTitle={{
        formatter: (options, route) =>
          `${options?.title ?? route?.name ?? 'Tino'} - Tino`,
      }}
    >
      <View style={styles.navigatorContainer}>
        <Stack.Navigator
          initialRouteName={isAuthenticated ? 'TabNavigator' : (Platform.OS === 'web' ? 'Auth' : 'Splash')}
          screenOptions={{
            headerShown: false,
            ...(Platform.OS === 'web'
              ? getWebScreenOptions()
              : getNativeScreenOptions()),
          }}
        >
          {!isAuthenticated ? (
            <>
              <Stack.Screen
                name="Splash"
                component={SplashScreen}
                options={getScreenConfig('splash')}
              />

              <Stack.Screen
                name="Auth"
                component={AuthScreen}
                options={getScreenConfig('auth')}
              />

              <Stack.Screen
                name="Register"
                component={RegisterScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="RegisterStep2"
                component={RegisterStep2Screen}
                options={getScreenConfig('registerStep')}
              />

              <Stack.Screen
                name="RegisterStep3"
                component={RegisterStep3Screen}
                options={getScreenConfig('registerStep')}
              />
            </>
          ) : (
            <>
              <Stack.Screen
                name="TabNavigator"
                component={TabNavigator}
                options={getScreenConfig('main')}
              />

              <Stack.Screen
                name="ApplicationSettings"
                component={ApplicationSettingsScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="ContactDetails"
                component={ContactDetailsScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="MyProfile"
                component={MyProfileScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="RatingSummary"
                component={RatingSummaryScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="ParticipantRating"
                component={ParticipantRatingScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="ChatConversation"
                component={ChatConversationScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="MyAnnouncements"
                component={MyAnnouncementsScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="MyApplications"
                component={MyApplicationsScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="MyApplicationsCalendar"
                component={MyApplicationsCalendarScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="Wallet"
                component={WalletScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="ApplicationCreate"
                component={ApplicationCreateScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="ApplicationSent"
                component={ApplicationSentScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="AnnouncementApplications"
                component={AnnouncementApplicationsScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="UserProfile"
                component={UserProfileScreen}
                options={getScreenConfig('register')}
              />

              <Stack.Screen
                name="CreateAnnouncement"
                component={CreateAnnouncementScreen as any}
                options={getScreenConfig('register')}
              />
            </>
          )}
        </Stack.Navigator>
      </View>
    </NavigationContainer>
  );

  // Wrap with GestureHandlerRootView on native only
  if (Platform.OS === 'web') {
    return <View style={styles.container}>{content}</View>;
  }

  return (
    <GestureHandlerRootView style={styles.container}>
      {content}
    </GestureHandlerRootView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navigatorContainer: {
    flex: 1,
    width: '100%',
  },
});

export default AppNavigator;
